import { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { API_URL } from '../../../../config/api';

export default function RequestService() {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        company: '',
        country: 'Australia', // Default country set to match phone input default
        industry: '',
        message: ''
    });

    const [checkboxes, setCheckboxes] = useState({
        privacyConsent: false,
        marketingConsent: false
    });

    const [status, setStatus] = useState({
        loading: false,
        message: "",
        type: ""
    });

    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [recaptchaToken, setRecaptchaToken] = useState("");

    // OTP State
    const [isVerified, setIsVerified] = useState(false);
    const [otpMode, setOtpMode] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpStatus, setOtpStatus] = useState({ loading: false, message: "", type: "" });

    const handleSendOTP = async () => {
        if (!formData.email) {
            setOtpStatus({ loading: false, message: "Please enter your email address first.", type: "error" });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setOtpStatus({ loading: false, message: "Please enter a valid email address.", type: "error" });
            return;
        }

        setOtpStatus({ loading: true, message: "", type: "" });
        try {
            const res = await fetch(`${API_URL}/auth/request-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ identifier: formData.email, context: "contact" })
            });

            let data;
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                data = await res.json();
            } else {
                const text = await res.text();
                console.error("Non-JSON response:", res.status, text.substring(0, 200));
                data = { success: false, message: `Server error (${res.status}). Please try again.` };
            }

            if (data.success) {
                setOtpMode(true);
                setOtpStatus({ loading: false, message: "Verification code sent to your email.", type: "success" });
            } else {
                setOtpStatus({ loading: false, message: data.message || "Failed to send code.", type: "error" });
            }
        } catch (error) {
            console.error("❌ OTP Send Error:", error);
            const msg = error instanceof TypeError ? "Cannot connect to server. Please check your connection." : (error.message || "Network Error");
            setOtpStatus({ loading: false, message: msg, type: "error" });
        }
    };

    const handleVerifyOTP = async () => {
        if (!otp || otp.length < 6) {
            setOtpStatus({ loading: false, message: "Please enter a valid 6-digit code.", type: "error" });
            return;
        }

        setOtpStatus({ loading: true, message: "", type: "" });
        try {
            const res = await fetch(`${API_URL}/auth/verify-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: formData.email, otp, identifier: formData.email })
            });

            let data;
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                data = await res.json();
            } else {
                const text = await res.text();
                console.error("Non-JSON response:", res.status, text.substring(0, 200));
                data = { success: false, message: `Server error (${res.status}). Please try again.` };
            }

            if (data.success) {
                setIsVerified(true);
                setOtpMode(false);
                setOtpStatus({ loading: false, message: "Email verified successfully!", type: "success" });
                setTimeout(() => setOtpStatus({ loading: false, message: "", type: "" }), 3000);
            } else {
                setOtpStatus({ loading: false, message: data.message || "Invalid or expired code.", type: "error" });
            }
        } catch (error) {
            console.error("❌ OTP Verify Error:", error);
            const msg = error instanceof TypeError ? "Cannot connect to server. Please check your connection." : (error.message || "Network Error");
            setOtpStatus({ loading: false, message: msg, type: "error" });
        }
    };

    // Industry options
    const industries = [
        "Education",
        "Insurance",
        "Retail",
        "Travel and Logistics",
        "Healthcare and Life Sciences",
        "Public Services",
        "Communication and Media",
        "Banking",
        "Manufacturing",
        "Engineering and Construction",
        "All Other Businesses"
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePhoneChange = (value, country) => {
        setFormData(prev => ({
            ...prev,
            phoneNumber: '+' + value, // Store with + prefix
            country: country.name // Auto-fill country name based on phone selection
        }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setCheckboxes(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate privacy consent
        if (!checkboxes.privacyConsent) {
            alert('Please accept the privacy policy to continue.');
            return;
        }

        if (!isVerified) {
            setStatus({ loading: false, message: "Please verify your email address before submitting.", type: "error" });
            return;
        }

        if (!recaptchaToken) {
            setStatus({ loading: false, message: "Please complete the reCAPTCHA verification.", type: "error" });
            return;
        }

        setStatus({ loading: true, message: "", type: "" });

        const baseUrl = API_URL;

        try {
            const res = await fetch(`${baseUrl}/service/requestService`, {
                method: "POST",
                body: JSON.stringify({
                    ...formData,
                    privacyConsent: checkboxes.privacyConsent,
                    marketingConsent: checkboxes.marketingConsent,
                    recaptchaToken
                }),
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });

            const data = await res.json();

            if (res.ok) {
                setStatus({
                    loading: false,
                    message: "Service request submitted successfully! We'll get back to you soon.",
                    type: "success"
                });

                // Reset form
                setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phoneNumber: '',
                    company: '',
                    country: '',
                    industry: '',
                    message: ''
                });
                setCheckboxes({
                    privacyConsent: false,
                    marketingConsent: false
                });

                setRecaptchaToken("");
                setIsVerified(false);
                setOtpMode(false);
                setOtp("");

                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }

                // Clear success message after 5 seconds
                setTimeout(() => {
                    setStatus({ loading: false, message: "", type: "" });
                }, 5000);
            } else {
                throw new Error(data.message || "Failed to submit service request");
            }
        } catch (error) {
            console.error("Error:", error);
            setStatus({
                loading: false,
                message: "Failed to submit request. Please try again.",
                type: "error"
            });
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ backgroundColor: '#000000e0' }}>
            {/* Subtle background decoration */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 right-20 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
            </div>

            <div className="w-full max-w-4xl relative z-10 pb-12">
                {/* Header */}
                <div className="text-center mb-10">
                    <div className="inline-block p-3 bg-purple-500/10 rounded-2xl mb-4">
                        <svg className="w-12 h-12 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <p className="text-gray-300 md:text-lg">Tell us about your project and we'll help bring it to life</p>
                </div>

                {/* Form Card */}
                <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 md:p-10 shadow-2xl border border-white/10">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* First Name and Last Name Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* First Name */}
                            <div className="relative">
                                <input
                                    type="text"
                                    id="firstName"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    required
                                    disabled={status.loading}
                                    className="peer w-full px-4 py-3 pt-6 bg-white/5 border border-gray-600 rounded-xl text-white placeholder-transparent focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300 disabled:opacity-50"
                                    placeholder="First Name"
                                />
                                <label
                                    htmlFor="firstName"
                                    className="absolute left-4 top-2 text-xs text-gray-300 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-500 peer-focus:top-2 peer-focus:text-xs peer-focus:text-purple-400"
                                >
                                    First Name *
                                </label>
                            </div>

                            {/* Last Name */}
                            <div className="relative">
                                <input
                                    type="text"
                                    id="lastName"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    required
                                    disabled={status.loading}
                                    className="peer w-full px-4 py-3 pt-6 bg-white/5 border border-gray-600 rounded-xl text-white placeholder-transparent focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300 disabled:opacity-50"
                                    placeholder="Last Name"
                                />
                                <label
                                    htmlFor="lastName"
                                    className="absolute left-4 top-2 text-xs text-gray-300 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-500 peer-focus:top-2 peer-focus:text-xs peer-focus:text-purple-400"
                                >
                                    Last Name *
                                </label>
                            </div>
                        </div>

                        {/* Email and Phone Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Email Address */}
                            <div className="relative group">
                                <div className="relative">
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        disabled={status.loading || isVerified}
                                        className="peer w-full px-4 py-3 pt-6 pr-28 bg-white/5 border border-gray-600 rounded-xl text-white placeholder-transparent focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300 disabled:opacity-50"
                                        placeholder="Email Address"
                                    />
                                    <label
                                        htmlFor="email"
                                        className="absolute left-4 top-2 text-xs text-gray-300 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-500 peer-focus:top-2 peer-focus:text-xs peer-focus:text-purple-400"
                                    >
                                        Email Address *
                                    </label>

                                    {/* Action Button / Tick Mark */}
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center h-full pt-2">
                                        {formData.email && !isVerified && !otpMode && (
                                            <button
                                                type="button"
                                                onClick={handleSendOTP}
                                                disabled={otpStatus.loading}
                                                className="flex items-center justify-center w-9 h-9 bg-purple-600/20 hover:bg-purple-600 text-purple-400 hover:text-white rounded-lg border border-purple-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                title="Verify Email"
                                            >
                                                {otpStatus.loading ? (
                                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                            </button>
                                        )}
                                        {(isVerified || otpMode) && (
                                            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 shadow-[0_0_15px_rgba(34,197,94,0.2)] animate-in fade-in zoom-in duration-500">
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {otpMode && (
                                    <div className="mt-4 p-4 bg-white/5 border border-white/10 rounded-xl space-y-4">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                maxLength={6}
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value)}
                                                className="peer w-full px-4 py-3 bg-white/5 border border-gray-600 rounded-xl text-white text-center text-2xl tracking-widest focus:outline-none focus:border-purple-500 transition-all"
                                                placeholder="000000"
                                            />
                                            <label className="block text-center text-xs text-gray-400 mt-2 uppercase tracking-wider">
                                                Enter 6-digit Code
                                            </label>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={handleVerifyOTP}
                                                disabled={otpStatus.loading}
                                                className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50"
                                            >
                                                {otpStatus.loading ? "Verifying..." : "Verify Code"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setOtpMode(false)}
                                                className="px-4 py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-all"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {otpStatus.message && !isVerified && (
                                    <p className={`mt-2 text-sm ${otpStatus.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                                        {otpStatus.message}
                                    </p>
                                )}
                            </div>

                            {/* Phone Number with react-phone-input-2 */}
                            <div className="relative phone-wrapper">
                                <PhoneInput
                                    country={'au'}
                                    value={formData.phoneNumber.replace('+', '')}
                                    onChange={handlePhoneChange}
                                    disabled={status.loading}
                                    enableSearch={true}
                                    searchPlaceholder="Search country..."
                                    containerClass="phone-input-container"
                                    inputClass="phone-input-field"
                                    buttonClass="phone-dropdown-button"
                                    dropdownClass="phone-dropdown"
                                    searchClass="phone-search"
                                />
                            </div>
                        </div>

                        {/* Company and Country Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Company */}
                            <div className="relative">
                                <input
                                    type="text"
                                    id="company"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleChange}
                                    required
                                    disabled={status.loading}
                                    className="peer w-full px-4 py-3 pt-6 bg-white/5 border border-gray-600 rounded-xl text-white placeholder-transparent focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300 disabled:opacity-50"
                                    placeholder="Company"
                                />
                                <label
                                    htmlFor="company"
                                    className="absolute left-4 top-2 text-xs text-gray-300 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-500 peer-focus:top-2 peer-focus:text-xs peer-focus:text-purple-400"
                                >
                                    Company *
                                </label>
                            </div>

                            {/* Country - Auto-filled from phone selection */}
                            <div className="relative">
                                <input
                                    type="text"
                                    id="country"
                                    name="country"
                                    value={formData.country}
                                    onChange={handleChange}
                                    required
                                    disabled={status.loading}
                                    readOnly={formData.phoneNumber !== ''}
                                    className="peer w-full px-4 py-3 pt-6 bg-white/5 border border-gray-600 rounded-xl text-white placeholder-transparent focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300 disabled:opacity-50 read-only:bg-white/10 read-only:cursor-not-allowed"
                                    placeholder="Country"
                                />
                                <label
                                    htmlFor="country"
                                    className="absolute left-4 top-2 text-xs text-gray-300 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-500 peer-focus:top-2 peer-focus:text-xs peer-focus:text-purple-400"
                                >
                                    Country * {formData.phoneNumber && <span className="text-purple-400">(Auto-filled)</span>}
                                </label>
                            </div>
                        </div>

                        {/* Industry Select */}
                        <div className="relative">
                            <select
                                id="industry"
                                name="industry"
                                value={formData.industry}
                                onChange={handleChange}
                                required
                                disabled={status.loading}
                                className="peer w-full px-4 py-3 pt-6 bg-white/5 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300 appearance-none cursor-pointer disabled:opacity-50"
                            >
                                <option value="" disabled className="bg-gray-900 text-gray-300">Select Industry</option>
                                {industries.map((industry) => (
                                    <option key={industry} value={industry} className="bg-gray-900 text-white">
                                        {industry}
                                    </option>
                                ))}
                            </select>
                            <label
                                htmlFor="industry"
                                className="absolute left-4 top-2 text-xs text-gray-300 transition-all peer-focus:text-purple-400"
                            >
                                Industry *
                            </label>
                            <svg
                                className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300 pointer-events-none"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>

                        {/* Message */}
                        <div className="relative">
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                required
                                disabled={status.loading}
                                rows={5}
                                className="peer w-full px-4 py-3 pt-6 bg-white/5 border border-gray-600 rounded-xl text-white placeholder-transparent focus:outline-none focus:border-purple-500 focus:bg-white/10 transition-all duration-300 resize-none disabled:opacity-50"
                                placeholder="Message"
                            />
                            <label
                                htmlFor="message"
                                className="absolute left-4 top-2 text-xs text-gray-300 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-500 peer-focus:top-2 peer-focus:text-xs peer-focus:text-purple-400"
                            >
                                Tell us about your project *
                            </label>
                        </div>

                        {/* Privacy Consent Checkbox */}
                        <div className="flex items-start space-x-3">
                            <div className="relative flex items-center justify-center mt-1">
                                <input
                                    type="checkbox"
                                    id="privacyConsent"
                                    name="privacyConsent"
                                    checked={checkboxes.privacyConsent}
                                    onChange={handleCheckboxChange}
                                    disabled={status.loading}
                                    className="w-5 h-5 appearance-none border-2 border-gray-500 rounded bg-transparent cursor-pointer checked:bg-purple-600 checked:border-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-[#000000e0] transition-all disabled:opacity-50"
                                />
                                {checkboxes.privacyConsent && (
                                    <svg
                                        className="absolute w-3 h-3 text-white pointer-events-none"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={3}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                )}
                            </div>
                            <label htmlFor="privacyConsent" className="text-sm text-gray-300 leading-relaxed cursor-pointer">
                                By checking this box, you consent to the collection and use of your personal information as outlined in our Privacy Policy. For further details on how your personal data will be processed and how your consent can be managed, refer to the SAN micron Privacy Notice. *
                            </label>
                        </div>

                        {/* Marketing Consent Checkbox */}
                        <div className="flex items-start space-x-3">
                            <div className="relative flex items-center justify-center mt-1">
                                <input
                                    type="checkbox"
                                    id="marketingConsent"
                                    name="marketingConsent"
                                    checked={checkboxes.marketingConsent}
                                    onChange={handleCheckboxChange}
                                    disabled={status.loading}
                                    className="w-5 h-5 appearance-none border-2 border-gray-500 rounded bg-transparent cursor-pointer checked:bg-purple-600 checked:border-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-[#000000e0] transition-all disabled:opacity-50"
                                />
                                {checkboxes.marketingConsent && (
                                    <svg
                                        className="absolute w-3 h-3 text-white pointer-events-none"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={3}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                )}
                            </div>
                            <label htmlFor="marketingConsent" className="text-sm text-gray-300 leading-relaxed cursor-pointer">
                                I would like to receive details about products, services and events from SAN micron.
                            </label>
                        </div>

                        {/* Success/Error Message */}
                        {status.message && (
                            <div className={`p-4 rounded-xl ${status.type === 'success'
                                ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                                : 'bg-red-500/20 border border-red-500/50 text-red-400'
                                }`}>
                                <div className="flex items-center gap-2">
                                    {status.type === 'success' ? (
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                    <span>{status.message}</span>
                                </div>
                            </div>
                        )}

                        {/* Captcha */}
                        <div className="flex justify-center flex-col items-center gap-4">
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                                theme="dark"
                                onChange={(token) => setRecaptchaToken(token || "")}
                            />
                            {!recaptchaToken && (
                                <p className="text-gray-500 text-xs">Verification required to prevent spam</p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <div className="flex justify-center pt-6">
                            <button
                                type="submit"
                                disabled={status.loading || !isVerified || !recaptchaToken}
                                className="px-12 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-[#000000e0] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            >
                                {status.loading ? "Submitting..." : "Submit Request"}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Footer Note */}
                <p className="text-center text-gray-300 text mt-6">
                    We'll review your request and get back to you within 24-48 hours. Or call us directly at <a href="tel:+61391167215" className="text-purple-400 hover:underline">+61 391 167 215</a>.
                </p>
            </div>

            {/* Custom Styles for react-phone-input-2 */}
            <style>{`
                .phone-input-container {
                    width: 100% !important;
                    font-family: inherit;
                }
                .phone-input-field {
                    width: 100% !important;
                    height: 58px !important;
                    background: rgba(255, 255, 255, 0.05) !important;
                    border: 1px solid #4b5563 !important;
                    border-radius: 0.75rem !important;
                    color: white !important;
                    font-size: 1rem !important;
                    padding-left: 58px !important;
                    transition: all 0.3s ease;
                }
                .phone-input-field:focus {
                    border-color: #a855f7 !important;
                    background: rgba(255, 255, 255, 0.1) !important;
                    box-shadow: none !important;
                }
                .phone-dropdown-button {
                    background: transparent !important;
                    border: 1px solid #4b5563 !important;
                    border-right: none !important;
                    border-radius: 0.75rem 0 0 0.75rem !important;
                    width: 48px !important;
                    height: 58px !important;
                    transition: all 0.3s ease;
                }
                .phone-input-container:focus-within .phone-dropdown-button {
                    border-color: #a855f7 !important;
                }
                .phone-dropdown-button .selected-flag {
                    width: 48px !important;
                    height: 100% !important;
                    padding: 0 !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    background: transparent !important;
                }
                .phone-dropdown-button:hover, .phone-dropdown-button.open {
                    background: rgba(255, 255, 255, 0.1) !important;
                }
                .phone-dropdown {
                    background: #111827 !important;
                    border: 1px solid #374151 !important;
                    color: white !important;
                    border-radius: 0.75rem !important;
                    margin-top: 8px !important;
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5) !important;
                    width: 300px !important;
                }
                .phone-dropdown .country:hover {
                    background: #1f2937 !important;
                }
                .phone-dropdown .country.highlight {
                    background: #374151 !important;
                }
                .phone-search {
                    background: #111827 !important;
                    color: white !important;
                    border-bottom: 1px solid #374151 !important;
                    padding: 10px !important;
                }
                .phone-search-input {
                    background: #1f2937 !important;
                    border: 1px solid #4b5563 !important;
                    color: white !important;
                    border-radius: 0.5rem !important;
                }
            `}</style>
        </div>
    );
}